import type { ComponentProps, HTMLAttributes } from 'react';

import { IconWeather } from '#icons/IconWeather';
import { WeatherIcon } from '#types/enums';
import { logger } from '#utils/logging/logger';

type Props = {
	name: WeatherIcon | null;
} & HTMLAttributes<SVGElement>;

export function WeatherIconComponent({ name, ...rest }: Props) {
	let iconName: ComponentProps<typeof IconWeather>['icon'];
	let title: string;

	switch (name) {
		case WeatherIcon.Clear:
			iconName = 'clear-day';
			title = 'Wolkenlos';
			break;

		case WeatherIcon.ClearNight:
			iconName = 'clear-night';
			title = 'Wolkenlos';
			break;

		case WeatherIcon.Cloudy:
			iconName = 'cloudy-day';
			title = 'Wolkig';
			break;

		case WeatherIcon.CloudyNight:
			iconName = 'cloudy-night';
			title = 'Wolkig';
			break;

		case WeatherIcon.Dewy:
			iconName = 'warning-danger';
			title = 'Tauwetter';
			break;

		case WeatherIcon.DewyNight:
			iconName = 'warning-danger';
			title = 'Tauwetter';
			break;

		case WeatherIcon.Flood:
		case WeatherIcon.FloodNight:
			iconName = 'warning-flood';
			title = 'Hochwasser';
			break;

		case WeatherIcon.Fog:
			iconName = 'foggy';
			title = 'Nebel';
			break;

		case WeatherIcon.FogNight:
			iconName = 'foggy-night';
			title = 'Nebel';
			break;

		case WeatherIcon.FogFreezingNight:
		case WeatherIcon.FogFreezing:
			iconName = 'warning-freezing-streets';
			title = 'Nebel (Gefrierend)';
			break;

		case WeatherIcon.GroundFog:
		case WeatherIcon.GroundFogNight:
			iconName = 'foggy';
			title = 'Bodennebel';
			break;

		case WeatherIcon.HighFog:
			iconName = 'foggy';
			title = 'Hochnebel';
			break;

		case WeatherIcon.HighFogNight:
			iconName = 'foggy-clouds-night';
			title = 'Hochnebel';
			break;

		case WeatherIcon.HighFogMountains:
		case WeatherIcon.HighFogMountainsNight:
			iconName = 'foggy';
			title = 'Hochnebel (Berge)';
			break;

		case WeatherIcon.HighFogRain:
			iconName = 'cloudy-foggy';
			title = 'Hochnebel & Regen';
			break;

		case WeatherIcon.HighFogRainNight:
			iconName = 'cloudy-foggy';
			title = 'Hochnebel & Regen';
			break;

		case WeatherIcon.Hail:
			iconName = 'hail-day';
			title = 'Hagelschauer';
			break;

		case WeatherIcon.HailNight:
			iconName = 'hail-night';
			title = 'Hagelschauer';
			break;

		case WeatherIcon.HailOvercast:
		case WeatherIcon.HailOvercastNight:
			iconName = 'hail';
			title = 'Hagelschauer (Bedeckt)';
			break;

		case WeatherIcon.Hazy:
			iconName = 'foggy-day';
			title = 'Dunstig';
			break;

		case WeatherIcon.HazyNight:
			iconName = 'foggy-night';
			title = 'Dunstig';
			break;

		case WeatherIcon.LightOvercast:
			iconName = 'mostly-clear-day';
			title = 'Leicht Bewölkt';
			break;

		case WeatherIcon.LightOvercastNight:
			iconName = 'mostly-clear-night';
			title = 'Leicht Bewölkt';
			break;

		case WeatherIcon.LightRainOvercast:
		case WeatherIcon.LightRainOvercastNight:
			iconName = 'rain';
			title = 'Regen & Leicht Bewölkt';
			break;

		case WeatherIcon.LightSnowOvercast:
		case WeatherIcon.LightSnowOvercastNight:
			iconName = 'snow';
			title = 'Schnee & Leicht Bewölkt';
			break;

		case WeatherIcon.NearlyClear:
			iconName = 'mostly-clear-day';
			title = 'Fast Wolkenlos';
			break;

		case WeatherIcon.NearlyClearNight:
			iconName = 'mostly-clear-night';
			title = 'Fast Wolkenlos';
			break;

		case WeatherIcon.Orcan:
		case WeatherIcon.OrcanNight:
			iconName = 'warning-west-storm';
			title = 'Orkan';
			break;

		case WeatherIcon.Overcast:
		case WeatherIcon.OvercastNight:
			iconName = 'heavy-cloudy';
			title = 'Bedeckt';
			break;

		case WeatherIcon.Rain:
			iconName = 'rain-day';
			title = 'Regen';
			break;

		case WeatherIcon.RainNight:
			iconName = 'rain-night';
			title = 'Regen';
			break;

		case WeatherIcon.RainClear:
			iconName = 'rain-day';
			title = 'Regenschauer & Heiter';
			break;

		case WeatherIcon.RainClearNight:
			iconName = 'rain-night';
			title = 'Regenschauer & Heiter';
			break;

		case WeatherIcon.RainFreezy:
		case WeatherIcon.RainFreezyNight:
			iconName = 'warning-freezing-streets';
			title = 'Regen (Gefrierend)';
			break;

		case WeatherIcon.RainOvercast:
		case WeatherIcon.RainOvercastNight:
			iconName = 'rain';
			title = 'Regen & Bedeckt';
			break;

		case WeatherIcon.RainSleetOvercast:
		case WeatherIcon.RainSleetOvercastNight:
			iconName = 'heavy-snow-rain';
			title = 'Schneeregen & Bedeckt';
			break;

		case WeatherIcon.Sleet:
			iconName = 'heavy-snow-rain-day';
			title = 'Schneeregenschauer';
			break;

		case WeatherIcon.SleetNight:
			iconName = 'heavy-snow-rain-night';
			title = 'Schneeregenschauer';
			break;

		case WeatherIcon.Snow:
			iconName = 'snow-day';
			title = 'Schneeschauer';
			break;

		case WeatherIcon.SnowNight:
			iconName = 'snow-night';
			title = 'Schneeschauer';
			break;

		case WeatherIcon.SnowClear:
			iconName = 'snow-day';
			title = 'Schneeschauer & Heiter';
			break;

		case WeatherIcon.SnowClearNight:
			iconName = 'snow-night';
			title = 'Schneeschauer & Heiter';
			break;

		case WeatherIcon.SnowOvercast:
		case WeatherIcon.SnowOvercastNight:
			iconName = 'snow';
			title = 'Schnee & Bedeckt';
			break;

		case WeatherIcon.SoftHail:
			iconName = 'heavy-snow';
			title = 'Graupelschauer';
			break;

		case WeatherIcon.SoftHailNight:
			iconName = 'heavy-snow-night';
			title = 'Graupelschauer';
			break;

		case WeatherIcon.SoftHailClear:
			iconName = 'heavy-snow-day';
			title = 'Graupelschauer & Heiter';
			break;

		case WeatherIcon.SoftHailClearNight:
			iconName = 'heavy-snow-night';
			title = 'Graupelschauer & Heiter';
			break;

		case WeatherIcon.SoftHailOvercast:
		case WeatherIcon.SoftHailOvercastNight:
			iconName = 'heavy-snow';
			title = 'Graupelschauer & Bedeckt';
			break;

		case WeatherIcon.Storm:
		case WeatherIcon.StormNight:
			iconName = 'warning-west-storm';
			title = 'Sturm';
			break;

		case WeatherIcon.StormRain:
		case WeatherIcon.StormRainNight:
			iconName = 'warning-danger';
			title = 'Sturm & Regen';
			break;

		case WeatherIcon.StrongOvercast:
			iconName = 'cloudy-day';
			title = 'Stark Bewölkt';
			break;

		case WeatherIcon.StrongOvercastNight:
			iconName = 'cloudy-night';
			title = 'Stark Bewölkt';
			break;

		case WeatherIcon.StrongRain:
			iconName = 'heavy-rain-day';
			title = 'Starker Regenschauer';
			break;

		case WeatherIcon.StrongRainNight:
			iconName = 'heavy-rain-night';
			title = 'Starker Regenschauer';
			break;

		case WeatherIcon.StrongRainOvercast:
		case WeatherIcon.StrongRainOvercastNight:
			iconName = 'heavy-rain';
			title = 'Starker Regenschauer & Bedeckt';
			break;

		case WeatherIcon.StrongSnow:
			iconName = 'heavy-snow-day';
			title = 'Starker Schneeschauer';
			break;

		case WeatherIcon.StrongSnowNight:
			iconName = 'heavy-snow-night';
			title = 'Starker Schneeschauer';
			break;

		case WeatherIcon.StrongSnowOvercast:
		case WeatherIcon.StrongSnowOvercastNight:
			iconName = 'heavy-snow';
			title = 'Starker Schneeschauer & Bewölkt';
			break;

		case WeatherIcon.ThunderstormDryOvercast:
		case WeatherIcon.ThunderstormDryOvercastNight:
			iconName = 'heavy-thunderstorm';
			title = 'Gewitter (Trocken) & Bedeckt';
			break;

		case WeatherIcon.ThunderstormDrySunny:
			iconName = 'thunderstorm-day';
			title = 'Gewitter (Trocken) & Sonnig';
			break;

		case WeatherIcon.ThunderstormDrySunnyNight:
			iconName = 'thunderstorm-night';
			title = 'Gewitter (Trocken) & Klar';
			break;

		case WeatherIcon.ThunderstormHailOvercast:
		case WeatherIcon.ThunderstormHailOvercastNight:
			iconName = 'hail-thunderstorm';
			title = 'Gewitter & Hagel (Bedeckt)';
			break;

		case WeatherIcon.ThunderstormLightRain:
		case WeatherIcon.ThunderstormLightRainNight:
			iconName = 'heavy-thunderstorm';
			title = 'Gewitter & Leichter Regen';
			break;

		case WeatherIcon.ThunderstormRainHail:
		case WeatherIcon.ThunderstormRainHailNight:
			iconName = 'hail-thunderstorm';
			title = 'Gewitter, Regen & Hagel';
			break;

		case WeatherIcon.ThunderstormRainSunny:
			iconName = 'heavy-thunderstorm-day';
			title = 'Gewitter, Sonnig & Regen';
			break;

		case WeatherIcon.ThunderstormRainSunnyNight:
			iconName = 'heavy-thunderstorm-night';
			title = 'Gewitter, Sonnig & Regen';
			break;

		case WeatherIcon.ThunderstormSoftHailSunny:
			iconName = 'heavy-snow-thunderstorm-day';
			title = 'Gewitter, Graupel & Sonnig';
			break;

		case WeatherIcon.ThunderstormSoftHailSunnyNight:
			iconName = 'heavy-snow-thunderstorm-night';
			title = 'Gewitter, Graupel & Klar';
			break;

		case WeatherIcon.ThunderstormStrongRain:
		case WeatherIcon.ThunderstormStrongRainNight:
			iconName = 'heavy-thunderstorm';
			title = 'Gewitter & Starker Regen';
			break;

		case WeatherIcon.ForestFire:
		case WeatherIcon.ForestFireNight:
			iconName = 'warning-forest-fire';
			title = 'Waldbrand';
			break;

		default:
			logger.error(`Missing Weather Icon: %s.`, name);
			iconName = 'clear-day';
			title = 'Wolkenlos';
			break;
	}

	return <IconWeather icon={iconName} title={title} {...rest} />;
}
